import { Attachment } from '../../../TestResults.types';
import { FileType } from './AttachmentsTab.types';

export function getFileName(url: string): string {
  return new URL(url).pathname.split('/').pop() || 'unknown-file';
}

export function getFileType(file: string): FileType {
  const extension = file.split('.').pop()?.toLowerCase();

  if (!extension) return 'unknown';
  if (['pdf'].includes(extension)) return 'pdf';
  if (['plist'].includes(extension)) return 'plist';
  if (['mp4', 'webm', 'ogg'].includes(extension)) return 'video';
  if (['txt', 'json', 'xml', 'log'].includes(extension)) return 'text';
  if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return 'image';
  if (['zip', 'tar', 'gz', 'rar', '7z'].includes(extension)) return 'compressed';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) return 'doc';

  return 'unknown';
}

export function getMimeType(file: string): string {
  const extension = file.split('.').pop()?.toLowerCase();

  if (!extension) return 'application/octet-stream';

  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'plist':
      return 'application/x-plist';
    case 'mp4':
      return 'video/mp4';
    case 'webm':
      return 'video/webm';
    case 'ogg':
      return 'video/ogg';
    case 'txt':
      return 'text/plain';
    case 'json':
      return 'application/json';
    case 'xml':
      return 'application/xml';
    case 'log':
      return 'text/plain';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'zip':
      return 'application/zip';
    case 'tar':
      return 'application/x-tar';
    case 'gz':
      return 'application/gzip';
    case 'rar':
      return 'application/x-rar-compressed';
    case '7z':
      return 'application/x-7z-compressed';
    case 'doc':
    case 'docx':
      return 'application/msword';
    case 'xls':
    case 'xlsx':
      return 'application/vnd.ms-excel';
    case 'ppt':
    case 'pptx':
      return 'application/vnd.ms-powerpoint';
    default:
      return `application/${extension}`;
  }
}

export async function downloadAttachment(url: string, fileName: string) {
  try {
    // Try to fetch the file
    const response = await fetch(url);
    const blob = await response.blob();

    // Create download link with blob
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(objectUrl);
  } catch {
    // Fallback: direct link (works for signed URLs)
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export function orderAttachmentsByType(attachments: Attachment[]): Attachment[] {
  return [...attachments].sort((a, b) => {
    const aType = getFileType(getFileName(a.url));
    const bType = getFileType(getFileName(b.url));
    const isAMedia = aType === 'image' || aType === 'video';
    const isBMedia = bType === 'image' || bType === 'video';
    if (isAMedia && !isBMedia) return 1; // a is media, b is not, a goes after b
    if (!isAMedia && isBMedia) return -1; // b is media, a is not, b goes after a
    return 0; // both are media or both are not, keep original order
  });
}

export function isMediaAttachment(type: FileType) {
  return type === 'image' || type === 'video';
}
